/**
 * https://github.com/google/model-viewer/blob/master/packages/model-viewer/src/three-components/EnvironmentScene.ts
 */

import { BackSide, BoxGeometry, Mesh, MeshBasicMaterial, MeshStandardMaterial, PointLight, Scene } from "three";

function createAreaLightMaterial(intensity) {
  const material = new MeshBasicMaterial();
  material.color.setScalar(intensity);
  return material;
}

const neutral = {
  topLight: {
    intensity: 400,
    position: [0.5, 14.0, 0.5]
  },
  room: {
    position: [0.0, 13.2, 0.0],
    scale: [31.5, 28.5, 31.5]
  },
  boxes: [
    {
      position: [-10.906, -1.0, 1.846],
      rotation: -0.195,
      scale: [2.328, 7.905, 4.651]
    },
    {
      position: [-5.607, -0.754, -0.758],
      rotation: 0.994,
      scale: [1.97, 1.534, 3.955]
    },
    {
      position: [6.167, -0.16, 7.803],
      rotation: 0.561,
      scale: [3.927, 6.285, 3.687]
    },
    {
      position: [-2.017, 0.018, 6.124],
      rotation: 0.333,
      scale: [2.002, 4.566, 2.064]
    },
    {
      position: [2.291, -0.756, -2.621],
      rotation: -0.286,
      scale: [1.546, 1.552, 1.496]
    },
    {
      position: [-2.193, -0.369, -5.547],
      rotation: 0.516,
      scale: [3.875, 3.487, 2.986]
    }
  ],
  lights: [
    {
      intensity: 80,
      position: [-14.0, 10.0, 8.0],
      scale: [0.1, 2.5, 2.5]
    },
    {
      intensity: 80,
      position: [-14.0, 14.0, -4.0],
      scale: [0.1, 2.5, 2.5]
    },
    {
      intensity: 23,
      position: [14.0, 12.0, 0.0],
      scale: [0.1, 5.0, 5.0]
    },
    {
      intensity: 16,
      position: [0.0, 9.0, 14.0],
      scale: [5.0, 5.0, 0.1]
    },
    {
      intensity: 80,
      position: [7.0, 8.0, -14.0],
      scale: [2.5, 2.5, 0.1]
    },
    {
      intensity: 80,
      position: [-7.0, 16.0, -14.0],
      scale: [2.5, 2.5, 0.1]
    },
    {
      intensity: 1,
      position: [0.0, 20.0, 0.0],
      scale: [0.1, 0.1, 0.1]
    }
  ]
};

class RoomEnvironment extends Scene {
  constructor() {
    super();

    const geometry = new BoxGeometry();
    geometry.deleteAttribute("uv");

    const roomMaterial = new MeshStandardMaterial({ side: BackSide });
    const boxMaterial = new MeshStandardMaterial();

    // Top light (mainLight)
    const topLight = new PointLight(0xffffff, neutral.topLight.intensity, 28, 2);
    topLight.position.set(...neutral.topLight.position);
    this.add(topLight);

    // Room
    const room = new Mesh(geometry, roomMaterial);
    room.position.set(...neutral.room.position);
    room.scale.set(...neutral.room.scale);
    this.add(room);

    // Boxes
    neutral.boxes.forEach(box => {
      const mesh = new Mesh(geometry, boxMaterial);
      mesh.position.set(...box.position);
      mesh.rotation.set(0, box.rotation, 0);
      mesh.scale.set(...box.scale);
      this.add(mesh);
    });

    // Area lights
    neutral.lights.forEach(light => {
      const mesh = new Mesh(geometry, createAreaLightMaterial(light.intensity));
      mesh.position.set(...light.position);
      mesh.scale.set(...light.scale);
      this.add(mesh);
    });
  }

  dispose() {
    const resources = new Set();

    this.traverse(object => {
      if (object.isMesh) {
        resources.add(object.geometry);
        resources.add(object.material);
      }
    });

    for (const resource of resources) {
      resource.dispose();
    }
  }
}

export { RoomEnvironment };
